const DEFAULT_TIME_ZONE = 'UTC'
const WEEKDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
}

export interface ZonedDateParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  weekday: number
}

function parseOffsetMinutes(offsetLabel: string): number {
  if (offsetLabel === 'GMT' || offsetLabel === 'UTC') return 0

  const match = offsetLabel.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/)
  if (!match) return 0

  const [, sign, hours, minutes] = match
  const total = Number(hours) * 60 + Number(minutes || '0')
  return sign === '-' ? -total : total
}

export function isValidTimeZone(timeZone?: string | null): boolean {
  if (!timeZone) return false
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date())
    return true
  } catch {
    return false
  }
}

export function normalizeTimeZone(timeZone?: string | null): string {
  return isValidTimeZone(timeZone) ? timeZone! : DEFAULT_TIME_ZONE
}

export function getViewerTimeZone(): string {
  return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
}

export function getEffectiveBusinessTimeZone(
  locationTimeZone?: string | null,
  restaurantTimeZone?: string | null
): string {
  if (isValidTimeZone(locationTimeZone)) return locationTimeZone!
  if (isValidTimeZone(restaurantTimeZone)) return restaurantTimeZone!
  return DEFAULT_TIME_ZONE
}

export function formatWallTime(time24: string): string {
  return time24
}

export function getTimeZoneAbbreviation(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: normalizeTimeZone(timeZone),
    timeZoneName: 'short',
  }).formatToParts(date)

  return parts.find((part) => part.type === 'timeZoneName')?.value ?? 'UTC'
}

function getOffsetMinutes(date: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: normalizeTimeZone(timeZone),
    timeZoneName: 'shortOffset',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)

  const offsetLabel = parts.find((part) => part.type === 'timeZoneName')?.value ?? 'GMT'
  return parseOffsetMinutes(offsetLabel)
}

export function getZonedDateParts(date: Date, timeZone: string): ZonedDateParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: normalizeTimeZone(timeZone),
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)

  const getPart = (type: string) => parts.find((part) => part.type === type)?.value ?? ''
  return {
    year: Number(getPart('year')),
    month: Number(getPart('month')),
    day: Number(getPart('day')),
    hour: Number(getPart('hour')),
    minute: Number(getPart('minute')),
    weekday: WEEKDAY_INDEX[getPart('weekday')] ?? 0,
  }
}

export function getIsoDateInTimeZone(date: Date, timeZone: string): string {
  const { year, month, day } = getZonedDateParts(date, timeZone)
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export function getApiWeekdayInTimeZone(date: Date, timeZone: string): number {
  return getZonedDateParts(date, timeZone).weekday
}

export function formatAbsoluteTimeInZone(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: normalizeTimeZone(timeZone),
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZoneName: 'short',
  }).format(date)
}

export function zonedWallTimeToUtcDate(
  parts: Pick<ZonedDateParts, 'year' | 'month' | 'day'>,
  time24: string,
  timeZone: string
): Date {
  const [hourStr, minuteStr] = time24.split(':')
  const hour = Number(hourStr)
  const minute = Number(minuteStr)
  const baseUtc = Date.UTC(parts.year, parts.month - 1, parts.day, hour, minute, 0, 0)

  let candidate = baseUtc
  for (let i = 0; i < 3; i += 1) {
    const offsetMinutes = getOffsetMinutes(new Date(candidate), timeZone)
    const nextCandidate = baseUtc - offsetMinutes * 60 * 1000
    if (nextCandidate === candidate) break
    candidate = nextCandidate
  }

  return new Date(candidate)
}

function addDays(parts: Pick<ZonedDateParts, 'year' | 'month' | 'day'>, deltaDays: number) {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + deltaDays))
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  }
}

export function getRelativeBusinessDate(
  timeZone: string,
  dayOffset = 0,
  now = new Date()
): Pick<ZonedDateParts, 'year' | 'month' | 'day' | 'weekday'> {
  const current = getZonedDateParts(now, timeZone)
  const nextDate = addDays(current, dayOffset)
  const weekdayDate = new Date(Date.UTC(nextDate.year, nextDate.month - 1, nextDate.day, 12, 0, 0, 0))
  return {
    ...nextDate,
    weekday: getApiWeekdayInTimeZone(weekdayDate, 'UTC'),
  }
}

export function getNextOccurrenceForWeekday(
  apiDay: number,
  timeZone: string,
  now = new Date()
): Pick<ZonedDateParts, 'year' | 'month' | 'day'> {
  const current = getZonedDateParts(now, timeZone)
  const delta = (apiDay - current.weekday + 7) % 7
  return addDays(current, delta)
}

export function buildViewerRangeLabel(
  apiDay: number,
  openTime: string,
  closeTime: string,
  businessTimeZone: string,
  viewerTimeZone: string,
  now = new Date()
): string | null {
  const normalizedBusinessTz = normalizeTimeZone(businessTimeZone)
  const normalizedViewerTz = normalizeTimeZone(viewerTimeZone)
  if (normalizedBusinessTz === normalizedViewerTz) return null

  const dateParts = getNextOccurrenceForWeekday(apiDay, normalizedBusinessTz, now)
  const openDate = zonedWallTimeToUtcDate(dateParts, openTime, normalizedBusinessTz)
  const closeDate = zonedWallTimeToUtcDate(dateParts, closeTime, normalizedBusinessTz)

  const openLabel = formatAbsoluteTimeInZone(openDate, normalizedViewerTz)
  const closeLabel = formatAbsoluteTimeInZone(closeDate, normalizedViewerTz)
  return `${openLabel} - ${closeLabel} your time`
}
